import * as React from 'react';
import { XIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

const tagVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap shrink-0 w-fit border transition-[color,box-shadow] [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-badge-default-bg text-badge-default-fg',
        secondary: 'border-transparent bg-badge-secondary-bg text-badge-secondary-fg',
        outline: 'border-badge-outline-border text-badge-outline-fg',
      },
      size: {
        default: 'px-2.5 py-0.5 text-xs',
        sm: 'px-2 py-px text-2xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Tag({
  className,
  variant,
  size,
  onRemove,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof tagVariants> & {
    /** When provided, renders a removable "X" button that fires this callback. */
    onRemove?: () => void;
  }) {
  function handleRemove(event: React.MouseEvent<HTMLButtonElement>) {
    // Don't let the click bubble to a clickable/selectable parent (e.g. a chip
    // inside a button or list row).
    event.stopPropagation();
    onRemove?.();
  }

  return (
    <span
      data-slot="tag"
      className={cn(tagVariants({ variant, size }), className)}
      {...props}
    >
      {children}
      {onRemove ? (
        <button
          type="button"
          data-slot="tag-remove"
          aria-label="Remove"
          onClick={handleRemove}
          className={cn(
            '-mr-0.5 ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full',
            'opacity-70 transition-opacity hover:opacity-100',
            'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            '[&>svg]:pointer-events-none [&>svg]:size-3',
          )}
        >
          <XIcon />
        </button>
      ) : null}
    </span>
  );
}

export { Tag, tagVariants };
